import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { getConfig } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { SplashScreen } from 'src/components/loading-screen/splash-screen';

import { AuthGuard } from 'src/auth/guard';
import { HRAuthGuard } from 'src/auth/guard/hr-guard';

import { paths } from '../paths';
import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

const BasicInfoPage = lazy(() => import('src/pages/organization/subtabs/basicInfo'));
const PlanBillingPage = lazy(() => import('src/pages/pricing/billing-tab-page'));
const SettingMembersPage = lazy(() => import('src/pages/organization/subtabs/members/organisms'));
const InvitationsPage = lazy(() => import('src/pages/organization/subtabs/invitations/organisms'));
const KnowledgeBasePage = lazy(() =>
  import('src/pages/organization/subtabs/knowledge-base/index').then((module) => ({
    default: module.default,
  }))
);
const OrgSettingLayoutV2 = lazy(() =>
  import('src/pages/organization/org-setting-layout-v2').then((module) => ({
    default: module.default,
  }))
);
const InterviewerPersonaPage = lazy(() =>
  import('src/pages/organization/subtabs/interviewer-persona/index').then((module) => ({
    default: module.default,
  }))
);
const EmailTemplatesPage = lazy(() =>
  import('src/pages/organization/subtabs/email-templates').then((module) => ({
    default: module.default,
  }))
);
// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<SplashScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <OrgSettingLayoutV2>
      <SuspenseOutlet />
    </OrgSettingLayoutV2>
  </DashboardLayout>
);

//TODO: Optimize Re-rendering of JobNavData
export function getOrganizationSettingRoutes(): RouteObject[] {
  const CONFIG = getConfig();

  return [
    {
      path: 'organization-settings',
      element: CONFIG.auth.skip ? (
        dashboardLayout()
      ) : (
        <AuthGuard>
          <HRAuthGuard>{dashboardLayout()}</HRAuthGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={paths.orgSettings.info} replace />, index: true },
        {
          path: 'info',
          element: <BasicInfoPage />,
        },
        {
          path: 'billing',
          element: <PlanBillingPage />,
        },
        {
          path: 'members',
          element: <SettingMembersPage />,
        },
        {
          path: 'invitations',
          element: <InvitationsPage />,
        },
        {
          path: 'knowledge-base',
          element: <KnowledgeBasePage />,
        },
        {
          path: 'ai-persona',
          element: <InterviewerPersonaPage />,
        },
        {
          path: 'notification/email-templates',
          element: <EmailTemplatesPage />,
        },
        {
          path: 'notification',
          element: <Navigate to={paths.orgSettings.notifications.emailTemplates} replace />,
        }
      ],
    },
  ];
}
